import { useMemo, useState } from 'react';
import { Link, useParams } from '@tanstack/react-router';
import {
  useMe,
  useProjects,
  useRescheduleCalendarEvent,
  useWorkspace,
  useWorkspaceActivity,
  useWorkspaceCalendarEvents,
  useWorkspaceMembers,
  useWorkspaceStatuses,
  useWorkspaceTasks,
} from '@fluxter/shared';
import { toast } from 'sonner';
import { ActivityFeed } from '@/components/ActivityFeed';
import type { ActivityEntry } from '@/lib/format-activity';
import {
  CalendarEventDialog,
  type DialogMode,
} from '@/components/CalendarEventDialog';
import {
  CalendarView,
  type CalendarEventRow,
  type CalendarTaskRow,
  type CalendarViewMode,
} from '@/components/CalendarView';
import {
  computeEventsRangeForView,
  NewEventCtaButton,
} from '@/components/CalendarHelpers';
import { useSetTopBarSlots } from '@/components/TopBarSlots';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toIsoDate } from '@/lib/dates';
import type { StatusCategory } from '@/components/CalendarTaskChip';
import { cn } from '@/lib/utils';

interface MeData {
  data?: {
    id?: string;
    attributes?: { name?: string; email?: string };
  };
}

interface WorkspaceData {
  data?: { attributes?: { name?: string } };
}

interface TaskAttrs {
  title?: string;
  code?: string;
  status_id?: string;
  assignee_id?: string | null;
  due_at?: string | null;
  project_id?: string;
  project_slug?: string | null;
}

interface TaskItem {
  id: string;
  attributes?: TaskAttrs;
}

interface StatusAttrs {
  name?: string;
  color?: string;
  category?: StatusCategory;
}

interface MemberAttrs {
  name?: string;
  email?: string;
}

interface CalendarEventAttrs {
  title?: string;
  start_at?: string;
  end_at?: string | null;
  is_all_day?: boolean;
  location?: string | null;
  project_id?: string | null;
  project_name?: string | null;
  creator_id?: string;
  attendees?: ReadonlyArray<{ user_id?: string; name?: string | null; email?: string | null }>;
}

interface CalendarEventItem {
  id: string;
  attributes?: CalendarEventAttrs;
}

interface ProjectListAttrs {
  name?: string;
  slug?: string;
}

interface ProjectListItem {
  id: string;
  attributes?: ProjectListAttrs;
}

const greeting = (now: Date = new Date()): string => {
  const h = now.getHours();
  if (h < 5) return 'Good evening';
  if (h < 12) return 'Good morning';
  if (h < 18) return 'Good afternoon';
  return 'Good evening';
};

const firstName = (fullName: string | null | undefined, fallback: string | null | undefined): string => {
  const source = (fullName ?? fallback ?? '').trim();
  if (!source) return 'there';
  const parts = source.split(/\s+/);
  return parts[0] ?? source;
};

export const WorkspaceHomePage = () => {
  const { workspaceSlug } = useParams({ from: '/w/$workspaceSlug' });

  const me = useMe();
  const workspace = useWorkspace(workspaceSlug);
  const statuses = useWorkspaceStatuses(workspaceSlug);
  const members = useWorkspaceMembers(workspaceSlug);

  const meId = (me.data as MeData | undefined)?.data?.id;
  const meName = (me.data as MeData | undefined)?.data?.attributes?.name;
  const meEmail = (me.data as MeData | undefined)?.data?.attributes?.email;
  const workspaceName = (workspace.data as WorkspaceData | undefined)?.data?.attributes?.name;

  // Cross-project task query, scoped to the current user's assignments.
  // No date filter for v1 — the 500-row server cap is generous for any
  // realistic personal backlog. (See `app/BACKLOG.md` for the cursor-
  // pagination follow-up if this becomes a constraint.)
  const tasks = useWorkspaceTasks(workspaceSlug, meId ? { assignee_id: meId } : {});
  const activity = useWorkspaceActivity(workspaceSlug, 25);
  const projects = useProjects(workspaceSlug);
  const rescheduleEvent = useRescheduleCalendarEvent(workspaceSlug);

  // Lifted from CalendarView in 13c so the page can derive a narrow
  // events-fetch window from focal+view (refetched on navigate). Mirrors
  // the CalendarPage shape so both surfaces behave the same.
  const [view, setView] = useState<CalendarViewMode>('week');
  const [focal, setFocal] = useState<Date>(() => new Date());

  const eventsRange = useMemo(
    () => computeEventsRangeForView(view, focal),
    [view, focal],
  );
  const eventsQuery = useWorkspaceCalendarEvents(workspaceSlug, eventsRange);

  // Dialog state. `mode` doubles as the open flag — null = closed.
  const [dialogMode, setDialogMode] = useState<DialogMode | null>(null);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);

  const statusList =
    (statuses.data as { data?: Array<{ id: string; attributes?: StatusAttrs }> } | undefined)
      ?.data ?? [];
  const statusById = useMemo(
    () => new Map(statusList.map((s) => [s.id, s.attributes ?? {}])),
    [statusList],
  );

  const memberList =
    (members.data as { data?: Array<{ id: string; attributes?: MemberAttrs }> } | undefined)
      ?.data ?? [];
  const memberById = useMemo(
    () => new Map(memberList.map((m) => [m.id, m.attributes ?? {}])),
    [memberList],
  );

  const taskList = (tasks.data as { data?: TaskItem[] } | undefined)?.data ?? [];
  const eventList =
    (eventsQuery.data as { data?: CalendarEventItem[] } | undefined)?.data ?? [];

  const calendarEvents: CalendarEventRow[] = useMemo(
    () =>
      eventList
        .filter((e) => Boolean(e.attributes?.start_at))
        .map((e) => {
          const a = e.attributes ?? {};
          return {
            id: e.id,
            title: a.title ?? 'Untitled',
            startAt: a.start_at ?? '',
            endAt: a.end_at ?? null,
            isAllDay: Boolean(a.is_all_day),
            location: a.location ?? null,
            projectName: a.project_name ?? null,
            attendees: (a.attendees ?? []).map((p) => ({
              userId: p.user_id ?? '',
              name: p.name ?? null,
              email: p.email ?? null,
            })),
          };
        }),
    [eventList],
  );

  // Workspace-level project list for the dialog's project picker.
  const projectListData =
    (projects.data as { data?: ProjectListItem[] } | undefined)?.data ?? [];
  const dialogProjects = useMemo(
    () =>
      projectListData
        .filter((p) => Boolean(p.attributes?.slug && p.attributes?.name))
        .map((p) => ({
          id: p.id,
          slug: p.attributes?.slug ?? '',
          name: p.attributes?.name ?? '',
        })),
    [projectListData],
  );

  // Look up the editing event in raw API form so we have creator_id +
  // project_id + every attendee in the dialog's expected shape.
  const editingEvent = useMemo(() => {
    if (!editingEventId) return undefined;
    const row = eventList.find((e) => e.id === editingEventId);
    if (!row) return undefined;
    const a = row.attributes ?? {};
    return {
      id: row.id,
      title: a.title ?? 'Untitled',
      startAt: a.start_at ?? '',
      endAt: a.end_at ?? null,
      isAllDay: Boolean(a.is_all_day),
      location: a.location ?? null,
      projectName: a.project_name ?? null,
      attendees: (a.attendees ?? []).map((p) => ({
        userId: p.user_id ?? '',
        name: p.name ?? null,
        email: p.email ?? null,
      })),
      creatorId: a.creator_id ?? '',
      projectId: a.project_id ?? null,
    };
  }, [editingEventId, eventList]);

  const handleOpenEvent = (eventUlid: string): void => {
    setEditingEventId(eventUlid);
    setDialogMode('edit');
  };

  const handleNewEvent = (): void => {
    setEditingEventId(null);
    setDialogMode('create');
  };

  const closeDialog = (): void => {
    setDialogMode(null);
    setEditingEventId(null);
  };

  const handleRescheduleEvent = (input: {
    eventUlid: string;
    startAt: string;
    endAt: string | null;
  }): void => {
    rescheduleEvent.mutate(
      { eventUlid: input.eventUlid, start_at: input.startAt, end_at: input.endAt },
      {
        onError: (err) =>
          toast.error(err instanceof Error ? err.message : 'Could not reschedule event.'),
      },
    );
  };

  // Add the "+ New event" CTA to the workspace home topbar. Workspace
  // home doesn't have a per-page CTA today; this slot is otherwise empty.
  const topBarCta = useMemo(
    () => <NewEventCtaButton onClick={handleNewEvent} />,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  useSetTopBarSlots({ cta: topBarCta });

  // Glass redesign — Phase 6.
  // Map each task to a CalendarView row. Only tasks with a due_at are
  // surfaced (calendar can't render undated rows). The CalendarView
  // component derives overdue + by-date bucketing internally; we no
  // longer need bucketByDueDate or a separate OverdueLane here.
  const calendarTasks = useMemo<CalendarTaskRow[]>(
    () =>
      taskList
        .filter((t) => Boolean(t.attributes?.due_at))
        .map((t) => {
          const status = t.attributes?.status_id ? statusById.get(t.attributes.status_id) : undefined;
          const assignee = t.attributes?.assignee_id
            ? memberById.get(t.attributes.assignee_id)
            : undefined;
          return {
            id: t.id,
            code: t.attributes?.code ?? '',
            title: t.attributes?.title ?? 'Untitled',
            statusColor: status?.color ?? '#94a3b8',
            isComplete: status?.category === 'complete',
            assigneeId: t.attributes?.assignee_id ?? null,
            assigneeName: assignee?.name ?? null,
            assigneeEmail: assignee?.email ?? null,
            dueAt: t.attributes?.due_at ?? '',
          };
        }),
    [taskList, statusById, memberById],
  );

  const calendarMembers = useMemo(
    () =>
      memberList.map((m) => ({
        id: m.id,
        name: m.attributes?.name ?? null,
        email: m.attributes?.email ?? null,
      })),
    [memberList],
  );

  // Stats: today / overdue. Overdue rows feed both the lane component
  // and the "Overdue" stat card.
  const todayIso = toIsoDate(new Date());
  const dueTodayCount = useMemo(
    () =>
      taskList.filter((t) => {
        const due = t.attributes?.due_at;
        if (!due) return false;
        const status = t.attributes?.status_id ? statusById.get(t.attributes.status_id) : undefined;
        if (status?.category === 'complete') return false;
        return toIsoDate(new Date(due)) === todayIso;
      }).length,
    [taskList, statusById, todayIso],
  );

  // "Overdue" stat-card count — derived without building the full row
  // list since `<CalendarView>` owns the overdue lane internally now.
  const overdueCount = useMemo(() => {
    let n = 0;
    for (const t of taskList) {
      const due = t.attributes?.due_at;
      if (!due) continue;
      if (toIsoDate(new Date(due)) >= todayIso) continue;
      const status = t.attributes?.status_id ? statusById.get(t.attributes.status_id) : undefined;
      if (status?.category === 'complete') continue;
      n++;
    }
    return n;
  }, [taskList, statusById, todayIso]);

  const todayLabel = new Date().toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });

  return (
    <>
      <div className="space-y-6">
        {/* Glass redesign — Phase 7a.
            Greeting upgraded to the prototype's title scale (28px /
            600 / -0.025em — same as task-detail title) so it
            anchors the page. Sub-line uses foreground-faint per the
            sidebar / detail rail meta convention. */}
        <header>
          <h1 className="text-foreground text-[28px] font-semibold tracking-[-0.025em]">
            {greeting()}, {firstName(meName, meEmail)}
          </h1>
          <p className="text-foreground-faint mt-1 text-[12.5px]">
            {workspaceName ? `${workspaceName} · ${todayLabel}` : todayLabel}
          </p>
        </header>

        {/* Stats row — glass cards per Q3. */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <StatCard
            label="Due today"
            value={dueTodayCount}
            tone={dueTodayCount > 0 ? 'accent' : 'muted'}
          />
          <StatCard
            label="Overdue"
            value={overdueCount}
            tone={overdueCount > 0 ? 'destructive' : 'muted'}
          />
        </div>

        {/*
          Glass redesign — Phase 6.
          Calendar widget: replaces the previous controls + overdue
          lane + month/week grids with a single `<CalendarView>` that
          owns header, view switcher, filter chips, overdue lane, and
          all three views (Day / Week / Month). Workspace-scoped
          (my-tasks across projects) — same component used by the
          per-project CalendarPage, just fed a different task source.

          The component manages its own internal padding; -mx-6
          cancels the WorkspaceShell's px-6 so it claims edge-to-edge
          width, and a min-h target keeps it visually anchored on the
          page until the rest of the workspace home gets re-skinned
          in Phase 7.
        */}
        <div className="-mx-6 flex min-h-[640px] flex-col">
          {tasks.isLoading || statuses.isLoading ? (
            <Skeleton className="mx-6 h-[440px] w-auto" aria-label="Loading calendar" />
          ) : taskList.length === 0 ? (
            <div className="mx-6">
              <EmptyState workspaceSlug={workspaceSlug} />
            </div>
          ) : (
            <CalendarView
              tasks={calendarTasks}
              events={calendarEvents}
              members={calendarMembers}
              view={view}
              setView={setView}
              focal={focal}
              setFocal={setFocal}
              onOpenTask={(taskUlid) => {
                window.location.assign(`/w/${workspaceSlug}/t/${taskUlid}`);
              }}
              onOpenEvent={handleOpenEvent}
              onRescheduleEvent={handleRescheduleEvent}
            />
          )}
        </div>

        {/*
          Recent activity widget — closes the slice 5b backlog item
          ("Recent activity widget on workspace home"). Pulls the
          first page (25 entries) of the workspace activity feed,
          with a Load more button to fetch additional pages without
          leaving the dashboard.
        */}
        <ActivityFeed
          title="Recent activity"
          workspaceSlug={workspaceSlug}
          entries={flattenActivityPages(activity.data)}
          isLoading={activity.isLoading}
          emptyLabel="Nothing has happened in this workspace yet."
          footer={
            activity.hasNextPage ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => void activity.fetchNextPage()}
                disabled={activity.isFetchingNextPage}
              >
                {activity.isFetchingNextPage ? 'Loading…' : 'Load more'}
              </Button>
            ) : null
          }
        />
      </div>

      {dialogMode ? (
        <CalendarEventDialog
          open
          onClose={closeDialog}
          workspaceSlug={workspaceSlug}
          mode={dialogMode}
          event={dialogMode === 'edit' ? editingEvent : undefined}
          members={calendarMembers}
          projects={dialogProjects}
        />
      ) : null}
    </>
  );
};

interface ActivityPagesShape {
  pages?: Array<{ data?: ActivityEntry[] }>;
}

const flattenActivityPages = (data: unknown): ActivityEntry[] => {
  const shaped = data as ActivityPagesShape | undefined;
  const pages = shaped?.pages ?? [];
  return pages.flatMap((p) => p?.data ?? []);
};

// ──────────────────────────────────────────────────────────────────────
//  Stat card primitive
// ──────────────────────────────────────────────────────────────────────

interface StatCardProps {
  label: string;
  value: number;
  tone: 'muted' | 'accent' | 'destructive';
}

/**
 * Glass redesign — Phase 7a. Per Q3, glass cards (not flat).
 *
 * Tones:
 *   muted       — neutral foreground, used when value is 0
 *   accent      — brand orange, used for "Due today" when > 0
 *   destructive — red, used for "Overdue" when > 0
 *
 * Tone colors stay close to the prototype's `--brand-accent` /
 * destructive semantics so the page reads consistently with the
 * kanban / list dueAt color cues.
 */
const TONE_VALUE: Record<StatCardProps['tone'], string> = {
  muted: 'text-foreground',
  accent: 'text-[var(--brand-accent)]',
  destructive: 'text-destructive',
};

const StatCard = ({ label, value, tone }: StatCardProps) => (
  <div
    className={cn(
      'flex items-baseline justify-between gap-3 rounded-xl border px-4 py-3.5 backdrop-blur-md',
      'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)]',
      'shadow-[var(--glass-card-shadow)]',
    )}
  >
    <span className="text-foreground-faint text-[11px] font-semibold tracking-[0.06em] uppercase">
      {label}
    </span>
    <span
      className={cn(
        'text-[28px] font-semibold tracking-[-0.02em] tabular-nums',
        TONE_VALUE[tone],
      )}
    >
      {value}
    </span>
  </div>
);

// ──────────────────────────────────────────────────────────────────────
//  Empty state
// ──────────────────────────────────────────────────────────────────────

const EmptyState = ({ workspaceSlug }: { workspaceSlug: string }) => (
  <div
    className={cn(
      'rounded-xl border border-dashed p-12 text-center backdrop-blur-md',
      'border-[var(--glass-card-border)] bg-[var(--glass-card-bg)]',
    )}
  >
    <p className="text-foreground text-[15px] font-medium">
      Nothing on your calendar yet
    </p>
    <p className="text-foreground-faint mx-auto mt-1.5 max-w-md text-[12.5px]">
      Tasks assigned to you with a due date will show up here. Pick a project from the
      sidebar to create one — or open an existing task and set its due date.
    </p>
    <p className="text-foreground-muted mt-3 text-[11px]">
      <Link
        to="/w/$workspaceSlug"
        params={{ workspaceSlug }}
        className="hover:text-foreground transition-colors"
      >
        Refresh
      </Link>
    </p>
  </div>
);
